import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { Container, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import {
  useCreateRequestListFolderMutation,
  ListRequestListFoldersDocument,
} from '@app-types/blackroom-api';
import { DialogProps } from '@features/Dialog/types';

interface DialogCreateRequestListItemProps extends DialogProps {
  parentId?: string;
  parentLabel?: string;
}

function DialogCreateRequestListItem({
  parentId,
  parentLabel,
}: DialogCreateRequestListItemProps) {
  const { t } = useTranslation();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [createItem, { loading }] = useCreateRequestListFolderMutation({
    onCompleted: () => {
      notifications.show({
        title: t('RequestList:Common.notifications.createSuccess'),
        message: '',
        color: 'green',
      });
      dialogStore?.closeDialog?.('dialog-create-request-list-item');
    },
    onError: () => {
      notifications.show({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'red',
      });
    },
  });

  const handleConfirm = useCallback(() => {
    createItem({
      variables: {
        input: {
          workspaceId: workspace.id,
          name: name.trim(),
          description: description.trim() || undefined,
          parentId: parentId || undefined,
        },
      },
      refetchQueries: [ListRequestListFoldersDocument],
    });
  }, [workspace.id, name, description, parentId, createItem]);

  return (
    <Container w={600} fluid>
      <Stack gap="lg" px="md" pb="lg">
        {parentLabel && (
          <Text fz={13} c="dimmed">
            {t('RequestList:DialogCreateRequestListItem.parentLabel', {
              parent: parentLabel,
            })}
          </Text>
        )}
        <TextInput
          label={t('RequestList:DialogCreateRequestListItem.nameLabel')}
          placeholder={t(
            'RequestList:DialogCreateRequestListItem.namePlaceholder',
          )}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          data-autofocus
        />
        <Textarea
          label={t('RequestList:DialogCreateRequestListItem.descriptionLabel')}
          placeholder={t(
            'RequestList:DialogCreateRequestListItem.descriptionPlaceholder',
          )}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          autosize
          minRows={4}
          maxRows={8}
        />
      </Stack>
      <ConfirmBar
        labelProps={{
          confirm: t('Common:cta.save'),
          cancel: t('Common:cta.cancel'),
        }}
        onConfirm={handleConfirm}
        onCancel={() =>
          dialogStore?.closeDialog?.('dialog-create-request-list-item')
        }
        disabled={!name.trim()}
        saving={loading}
      />
    </Container>
  );
}

export default DialogCreateRequestListItem;
